import { usePageFilterSaver } from 'hooks/usePageFilterSaver';
import { TableChoiceGroupFilter } from 'uikit';

import type { IAfterGuiAttachedParams } from 'ag-grid-community';
import { type CustomFilterProps, useGridFilter } from 'ag-grid-react';
import { useCallback, useMemo, useRef } from 'react';

type FilterProps = CustomFilterProps & {
    changeFilterHandler: (values: any) => void;
    filterItems: { name: string; value: string }[];
};

export const AgGridChoiceFilterAdapter = (props: FilterProps) => {
    const filterRef = useRef<() => void | null>();

    const {
        changeFilterHandler,
        colDef: { field: fieldName },
        filterItems,
        onModelChange,
        context,
    } = props;

    const doesFilterPass = useCallback(() => true, []);
    const afterGuiAttached = useCallback(({ hidePopup }: IAfterGuiAttachedParams) => {
        filterRef.current = hidePopup;
    }, []);

    useGridFilter({
        doesFilterPass,
        afterGuiAttached,
    });
    const { savedFilterState, handleSaveFilterState } = usePageFilterSaver({});

    const onConfirmHandler = ({ value }: any) => {
        const checkedValues = value ? [value] : [];

        if (context?.tableUniqueId) {
            handleSaveFilterState({
                key: fieldName,
                newState: checkedValues.map((item) => ({ value: item })),
                rootKey: context.tableUniqueId,
            });
        }

        changeFilterHandler({ fieldName, checkedValues });
        onCancelHandler();
        onModelChange?.({
            fieldName,
            checkedValues,
        });
    };

    const onCancelHandler = () => {
        filterRef?.current?.();
    };

    const activeFilterValue = useMemo(() => {
        if (context?.tableUniqueId && fieldName) {
            const savedFilterValues = savedFilterState?.[context?.tableUniqueId]?.[fieldName];

            if (savedFilterValues) {
                const value = savedFilterValues[0]?.value;

                return filterItems.find((item) => item.value === value);
            }
        }

        return undefined;
    }, [context?.tableUniqueId, fieldName, filterItems, savedFilterState]);

    return (
        <TableChoiceGroupFilter
            filterValue={activeFilterValue}
            onCancel={onCancelHandler}
            items={filterItems}
            onConfirm={onConfirmHandler}
        />
    );
};

AgGridChoiceFilterAdapter.displayName = 'AgGridChoiceFilterAdapter';
